/* ════════════════════════════════════════════════════════════════════════
   HOME OS SELECTORS — small derived helpers used by the Wall Display
   and other Home OS surfaces. All read-only, work on local state.
   ════════════════════════════════════════════════════════════════════════ */

import { CATEGORY_TO_MODULE, HOME_OS_MODULES } from "../data/homeOsModuleConfig";

function todayIso() { return new Date().toISOString().slice(0, 10); }

/* ── Today's important items ───────────────────────────────────────── */

export function getTodaysImportantItems(state, limit = 8) {
  const today = todayIso();
  const items = (state.tasks || [])
    .filter(t => t.dueDate === today)
    .sort((a, b) => {
      const s = (b.priority === "high" ? 1 : 0) - (a.priority === "high" ? 1 : 0);
      if (s !== 0) return s;
      return (a.dueTime || "").localeCompare(b.dueTime || "");
    })
    .slice(0, limit);
  if (items.length) return items.map(t => ({ ...t, module: HOME_OS_MODULES[CATEGORY_TO_MODULE[t.category] || "today"] }));
  // Fallback — pick the closest upcoming tasks
  return (state.tasks || [])
    .filter(t => t.dueDate && t.dueDate > today && t.status !== "Done")
    .sort((a, b) => (a.dueDate || "").localeCompare(b.dueDate || ""))
    .slice(0, limit)
    .map(t => ({ ...t, module: HOME_OS_MODULES[CATEGORY_TO_MODULE[t.category] || "today"] }));
}

/* ── Family mood summary ───────────────────────────────────────────── */

export function getFamilyMoodSummary(state) {
  const today = todayIso();
  const todays = (state.moodCheckins || []).filter(c => (c.createdAt || "").slice(0, 10) === today);
  const latestByMember = new Map();
  todays.forEach(c => {
    const cur = latestByMember.get(c.memberId);
    if (!cur || (c.createdAt || "") > (cur.createdAt || "")) latestByMember.set(c.memberId, c);
  });
  const eligible = (state.members || []).filter(m => m.ageGroup !== "Baby" && m.ageGroup !== "Pet");
  const memberMoods = eligible.map(m => ({ member: m, checkin: latestByMember.get(m.id) || null }));
  const zoneCounts = { yellow: 0, red: 0, green: 0, blue: 0, none: 0 };
  memberMoods.forEach(({ checkin }) => zoneCounts[checkin?.zone || "none"]++);
  return {
    total: eligible.length,
    checkedIn: latestByMember.size,
    memberMoods,
    zoneCounts,
  };
}

/* ── Next cleaning task ─────────────────────────────────────────────── */

export function getNextCleaningTask(state) {
  const open = (state.cleaningTasks || []).filter(t => t.status !== "Done");
  // Prefer assigned + daily, fall back to daily, then weekly
  const ranked = [...open].sort((a, b) => {
    const af = a.frequency === "Daily" ? 0 : a.frequency === "Weekly" ? 1 : 2;
    const bf = b.frequency === "Daily" ? 0 : b.frequency === "Weekly" ? 1 : 2;
    if (af !== bf) return af - bf;
    return (a.estimatedMinutes || 99) - (b.estimatedMinutes || 99);
  });
  const next = ranked[0] || null;
  const openCount = open.length;
  // Rooms by open task count
  const roomCounts = {};
  open.forEach(t => { roomCounts[t.room] = (roomCounts[t.room] || 0) + 1; });
  const topRoom = Object.entries(roomCounts).sort((a, b) => b[1] - a[1])[0];
  return { next, openCount, topRoom: topRoom ? { roomId: topRoom[0], count: topRoom[1] } : null };
}

/* ── Tonight's dinner status ────────────────────────────────────────── */

export function getTonightDinnerStatus(state) {
  const today = todayIso();
  // 1) Prefer weekly meal plan match
  const plan = state?.weeklyMealPlan || [];
  const dayName = ["Sun","Mon","Tue","Wed","Thu","Fri","Sat"][new Date().getDay()];
  const planEntry = plan.find(d => d.iso === today) || plan.find(d => d.day === dayName);
  if (planEntry && planEntry.dinner) {
    const recipe = (state?.recipes || []).find(r => r.id === planEntry.dinnerRecipeId) || null;
    return {
      status: planEntry.status === "Cooked" ? "Sorted" : (planEntry.status === "Needs decision" ? "Needs decision" : "Planned"),
      title: planEntry.dinner,
      assignedTo: planEntry.assignedCook,
      time: null, task: null, planEntry, recipe,
    };
  }
  // 2) Fallback: meal-category task today
  const mealTasks = (state.tasks || []).filter(t => t.category === "Meals" && t.dueDate === today);
  const dinner = mealTasks.find(t => /dinner/i.test(t.title) || (t.dueTime && t.dueTime >= "17:00"));
  if (dinner) {
    return {
      status: dinner.status === "Done" ? "Sorted" : (dinner.status === "Needs action" ? "Needs decision" : "Planned"),
      title: dinner.title,
      assignedTo: dinner.assignedTo,
      time: dinner.dueTime,
      task: dinner, planEntry: null, recipe: null,
    };
  }
  return { status: "Needs decision", title: "Dinner needs a decision", assignedTo: null, time: null, task: null, planEntry: null, recipe: null };
}

/* ── Approval / reminder items ──────────────────────────────────────── */

export function getApprovalItems(state, limit = 5) {
  const items = (state.tasks || [])
    .filter(t => t.approvalRequired && t.status !== "Done")
    .sort((a, b) => (a.dueDate || "").localeCompare(b.dueDate || ""))
    .slice(0, limit)
    .map(t => ({ ...t, module: HOME_OS_MODULES[CATEGORY_TO_MODULE[t.category] || "today"], kind: "approval" }));
  return items;
}

/* ── Wall calendar preview (Mon-Sun strip) ──────────────────────────── */

export function getWallCalendarPreview(state) {
  const now = new Date();
  const dayIdx = (now.getDay() + 6) % 7; // Mon=0
  const monday = new Date(now);
  monday.setDate(monday.getDate() - dayIdx);
  monday.setHours(0, 0, 0, 0);
  const days = [];
  const LABELS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
  for (let i = 0; i < 7; i++) {
    const d = new Date(monday);
    d.setDate(d.getDate() + i);
    const iso = d.toISOString().slice(0, 10);
    const dayTasks = (state.tasks || []).filter(t => t.dueDate === iso);
    const moduleSet = new Set();
    dayTasks.forEach(t => moduleSet.add(CATEGORY_TO_MODULE[t.category] || "today"));
    days.push({
      iso,
      label: LABELS[i],
      dayNumber: d.getDate(),
      isToday: i === dayIdx,
      count: dayTasks.length,
      modules: Array.from(moduleSet).slice(0, 4).map(id => HOME_OS_MODULES[id]),
    });
  }
  return days;
}

/* ── Chores summary ─────────────────────────────────────────────────── */

export function getChoresSummary(state, limit = 3) {
  const today = todayIso();
  const open = (state.tasks || [])
    .filter(t => t.category === "Chores" && t.status !== "Done")
    .sort((a, b) => (a.dueDate || "").localeCompare(b.dueDate || ""))
    .slice(0, limit);
  const assignedToday = new Set();
  (state.tasks || []).forEach(t => {
    if (t.category === "Chores" && t.dueDate === today && t.assignedTo) assignedToday.add(t.assignedTo);
  });
  return { openCount: (state.tasks || []).filter(t => t.category === "Chores" && t.status !== "Done").length, next: open, assignedToday: Array.from(assignedToday) };
}

/* ── Screensaver: upcoming items (today first, then nearest future) ──── */

export function getScreensaverUpcomingItems(state, limit = 3) {
  const today = todayIso();
  const upcoming = (state.tasks || [])
    .filter(t => t.dueDate && t.dueDate >= today && t.status !== "Done")
    .sort((a, b) => {
      const d = (a.dueDate || "").localeCompare(b.dueDate || "");
      if (d !== 0) return d;
      return (a.dueTime || "").localeCompare(b.dueTime || "");
    })
    .slice(0, limit)
    .map(t => ({ ...t, module: HOME_OS_MODULES[CATEGORY_TO_MODULE[t.category] || "today"] }));
  return upcoming;
}

/* ── Screensaver: household mood phrase ─────────────────────────────── */

export function getHouseholdMoodPhrase(state) {
  const summary = getFamilyMoodSummary(state);
  if (summary.checkedIn === 0) {
    return { phrase: "No check-ins yet today", checkedIn: 0, total: summary.total };
  }
  if (summary.zoneCounts.red >= 1) {
    return { phrase: "1 person may need support", checkedIn: summary.checkedIn, total: summary.total };
  }
  if (summary.zoneCounts.blue >= 2) {
    return { phrase: "Household feels low-energy", checkedIn: summary.checkedIn, total: summary.total };
  }
  if (summary.zoneCounts.yellow >= 2) {
    return { phrase: "Household has good energy", checkedIn: summary.checkedIn, total: summary.total };
  }
  return { phrase: "Household feels steady", checkedIn: summary.checkedIn, total: summary.total };
}

/* ── Screensaver: family photo carousel items ──────────────────────── */

export function getFamilyPhotoCarouselItems(state) {
  return (state.members || [])
    .filter(m => m.ageGroup !== "Pet" || m.avatarUrl) // show pets only if they have a photo
    .map(m => ({
      id: m.id,
      name: m.name,
      role: m.role,
      avatarUrl: m.avatarUrl || null,
      color: m.color,
      ageGroup: m.ageGroup,
    }));
}
